import {
  ComponentPropsWithoutRef,
  FC,
  MutableRefObject,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridApiPro,
} from '@mui/x-data-grid-pro';

import TableInstance from '@/shared/constants/tableInstance';

import WaveUINoDataOverlay from '../../utilities/WaveUINoDataOverlay';
import Action from './components/ActionButton/types/action.type';
import ColumnHeaderMenu from './components/ColumnHeaderMenu';
import { SnackToolbarProps } from './components/SnackToolbar';
import Toolbar from './components/Toolbar';
import useAssignPanelAnchor from './hooks/useAssignPanelAnchor';
import useSortableTableColumns from './hooks/useSortableTableColumns';
import useTableSnackBar from './hooks/useTableSnackBar';
import TableModels from './types/tableModels.type';
import { getLocaleText } from './utils/getLocaleText';

type DataGridProComponentType = ComponentPropsWithoutRef<typeof DataGridPro>;

type DataGridTableProps = DataGridProComponentType & {
  customProps: {
    toolbar?: {
      title?: string;
      toolbarActions?: ReactNode;
      toolbarFilters?: ReactNode;
      rowActions?: Action[];
    };
    snackBarProps?: Partial<SnackToolbarProps>;
    tableInstance: TableInstance;
    apiRef: MutableRefObject<GridApiPro>;
    model: TableModels;
  };
};

const DataGridTable: FC<DataGridTableProps> = ({ customProps, ...props }) => {
  const { t } = useTranslation('dataGrid');

  const { apiRef, model } = customProps;

  const assignPanelAnchorState = useAssignPanelAnchor({ apiRef });

  useTableSnackBar({
    apiRef,
    snackBarProps: customProps?.snackBarProps,
    tableInstance: customProps?.tableInstance,
  });

  const { columns, handleColumnOrderChange } = useSortableTableColumns({
    initialColumns: props.columns,
    rowActions: customProps?.toolbar?.rowActions,
    checkboxSelection: props?.checkboxSelection,
  });

  const defaultSettings: Partial<DataGridProComponentType> = {
    pagination: true,
    paginationMode: 'server',
    filterMode: 'server',
    sortingMode: 'server',
    checkboxSelection: true,
    localeText: getLocaleText(t),
    columnHeaderHeight: 60,
    disableColumnFilter: false,
    disableColumnSelector: false,
    disableColumnMenu: false,
    disableRowSelectionOnClick: true,
    initialState: {
      ...props.initialState,
      pinnedColumns: {
        left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
        right: ['actions'],
      },
      filter: {
        filterModel: model.filterModel,
      },
      sorting: {
        sortModel: model.sortModel,
      },
      pagination: {
        paginationModel: model.paginationModel,
      },
    },
    slots: {
      toolbar: Toolbar,
      columnMenuIcon: () => null,
      columnMenu: ColumnHeaderMenu,
      noRowsOverlay: WaveUINoDataOverlay,
    },
    slotProps: {
      panel: {
        anchorEl: () => assignPanelAnchorState.assignAnchor(),
      },
      toolbar: {
        ...customProps?.toolbar,
        setFiltersAnchor: assignPanelAnchorState.setFiltersAnchor,
        setColumnsAnchor: assignPanelAnchorState.setColumnsAnchor,
      },
    },
    onCellKeyDown: (_, e) => e.stopPropagation(),
  };

  return (
    <DataGridPro
      {...defaultSettings}
      {...props}
      columns={columns}
      onColumnOrderChange={handleColumnOrderChange}
      apiRef={apiRef}
    />
  );
};

export default DataGridTable;
